import React, { useEffect, useRef } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import SoloQuizResultsBody from './SoloQuizResultsBody';
import type { QuizAttemptResponse } from '../services/api';
import { playQuizSfxReward } from '../audio/quizSfx';

export interface SoloQuizResultsModalProps {
  show: boolean;
  hasTooManySkips: boolean;
  isQuizCompleted: boolean;
  /** True when the run ended without clearing all rounds (e.g. Tile Match / Genius loss). */
  finishedAsGameOver?: boolean;
  /** Resolved display title (e.g. getQuizDisplayTitle + i18n). */
  quizTitleDisplay: string;
  correctAnswers: number;
  totalQuestions: number;
  score: number;
  earnedXP: number;
  quizResponse: QuizAttemptResponse | null;
  resultDurationSeconds: number | null;
  onPlayAgain: () => void | Promise<void>;
  onBackToHome: () => void;
  /** Disables Play Again while a new solo session is starting. */
  playAgainLoading?: boolean;
  arenaPublishOffer?: boolean;
  arenaPublishChecked?: boolean;
  onArenaPublishCheckedChange?: (checked: boolean) => void;
  arenaPublishAvailabilityLoading?: boolean;
  arenaPublishBlocked?: boolean;
  /** Which hosted-open rule blocks publishing (tooltip copy). Duplicate quiz takes precedence over max rooms. */
  arenaPublishBlockedReason?: 'duplicateQuiz' | 'maxRooms';
  /** Server limit for {{max}} in max-rooms tooltip (e.g. from hosted-open summary). */
  arenaPublishMaxOpenRooms?: number;
}

/**
 * Shared solo quiz results UI (multiple-choice and WITC): stats grid, XP breakdown,
 * speed/VIP/app bonus messaging, PWA install teaser — keep both flows in sync.
 */
const SoloQuizResultsModal: React.FC<SoloQuizResultsModalProps> = ({
  show,
  hasTooManySkips,
  isQuizCompleted,
  finishedAsGameOver = false,
  quizTitleDisplay,
  correctAnswers,
  totalQuestions,
  score,
  earnedXP,
  quizResponse,
  resultDurationSeconds,
  onPlayAgain,
  onBackToHome,
  playAgainLoading = false,
  arenaPublishOffer = false,
  arenaPublishChecked = true,
  onArenaPublishCheckedChange,
  arenaPublishAvailabilityLoading = false,
  arenaPublishBlocked = false,
  arenaPublishBlockedReason,
  arenaPublishMaxOpenRooms,
}) => {
  const { t } = useSafeTranslation();
  const rewardPlayedRef = useRef(false);

  useEffect(() => {
    if (!show) {
      rewardPlayedRef.current = false;
      return;
    }
    if (!isQuizCompleted || hasTooManySkips) return;
    if (rewardPlayedRef.current) return;
    rewardPlayedRef.current = true;
    playQuizSfxReward();
  }, [show, isQuizCompleted, hasTooManySkips]);

  return (
    <Modal
      show={show}
      onHide={() => {}}
      backdrop="static"
      keyboard={false}
      fullscreen="md-down"
      className="solo-quiz-results-modal"
    >
      <Modal.Header>
        <Modal.Title>
          {hasTooManySkips
            ? t('quiz.tooManySkipped', 'Too Many Questions Skipped')
            : isQuizCompleted
              ? finishedAsGameOver
                ? t('quiz.gameOverResults', 'Game Over')
                : t('quiz.quizResults', 'Quiz Results')
              : t('quiz.quizAbandoned', 'Quiz Abandoned')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SoloQuizResultsBody
          hasTooManySkips={hasTooManySkips}
          isQuizCompleted={isQuizCompleted}
          finishedAsGameOver={finishedAsGameOver}
          quizTitleDisplay={quizTitleDisplay}
          correctAnswers={correctAnswers}
          totalQuestions={totalQuestions}
          score={score}
          earnedXP={earnedXP}
          quizResponse={quizResponse}
          resultDurationSeconds={resultDurationSeconds}
          arenaPublishOffer={arenaPublishOffer}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={onArenaPublishCheckedChange}
          arenaPublishAvailabilityLoading={arenaPublishAvailabilityLoading}
          arenaPublishBlocked={arenaPublishBlocked}
          arenaPublishBlockedReason={arenaPublishBlockedReason}
          arenaPublishMaxOpenRooms={arenaPublishMaxOpenRooms}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          variant="outline-primary"
          onClick={() => void onPlayAgain()}
          disabled={playAgainLoading}
        >
          {playAgainLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" role="status" />
              {t('quiz.playAgain', 'Play Again')}
            </>
          ) : (
            t('quiz.playAgain', 'Play Again')
          )}
        </Button>
        <Button type="button" variant="primary" onClick={onBackToHome}>
          {t('quiz.backToHome', 'Back to Home')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SoloQuizResultsModal;
